import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import FacultySection from "@/components/FacultySection";
import WallOfFameMarquee from "@/components/WallOfFameMarquee";
import { getCoursePricing, formatINR } from "@/lib/pricing";
import { toast } from "sonner";
import { Analytics } from "@/lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, MessageCircle, Star, ShieldCheck, Award, GraduationCap, Briefcase, Headphones,
  FileCheck, Clock, MapPin, Phone, Sparkles, PlayCircle, Users, Trophy, Radio, Calendar, Bell,
} from "lucide-react";

const WHATSAPP_URL = "https://wa.me/919131341638?text=Hi%20Kamlesh%20Sir%2C%20I%20want%20to%20know%20about%20your%20courses";
// Real photos of Kamlesh Sir (sourced from codewithkamlesh.in)
const HERO_IMG = "https://customer-assets.emergentagent.com/job_digital-scale-cwk/artifacts/ayfsnv1k_1%20%282%29.png";
const TRAINER_IMG = "https://customer-assets.emergentagent.com/job_batch-academy-2/artifacts/71ph5emp_WhatsApp%20Image%202026-05-18%20at%2017.44.52.jpeg";
const STUDENT_AVATARS = [
  "https://images.unsplash.com/photo-1603185030522-05d4497bb180?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1604177091072-b7b677a077f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
];

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

export default function Landing() {
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showCounselling, setShowCounselling] = useState(false);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data.slice(0, 3))).catch(() => {});
    api.get("/batches").then(({ data }) => setBatches(data || [])).catch(() => {});
  }, []);

  // Active = batches with status==="active" OR currently between start_date and end_date
  const nowMs = Date.now();
  const activeBatches = batches.filter((b) => {
    if (b.status === "active") return true;
    const s = new Date(b.start_date).getTime();
    const e = new Date(b.end_date).getTime();
    return s <= nowMs && nowMs <= e;
  });

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={fontStack}>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-white/5">
        {/* Decorative pink glow */}
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-[#FF0066]/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] bg-[#FF0066]/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF0066]/10 border border-[#FF0066]/30 text-[#FF0066] text-xs font-medium mb-5" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> Performance-Driven Digital Marketing Training
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" style={displayStack} data-testid="hero-headline">
              Grow Faster with{" "}
              <span className="text-[#FF0066]">Result-Driven</span>{" "}
              Digital Marketing Courses
              <span className="block text-white/80 text-2xl sm:text-3xl lg:text-4xl font-bold mt-2">
                <span className="text-[#FF0066]">(Online</span> + <span className="text-emerald-400">Offline)</span>
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg lg:text-xl text-white/70 leading-relaxed max-w-2xl">
              Master SEO, Google Ads, Meta Ads, WordPress, Social Media Marketing and AI Tools with live classes, real projects and one-on-one mentor support — based in Indore.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/courses" className="inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-6 py-3.5 transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.5)]" data-testid="hero-explore-btn">
                Explore Courses <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <button onClick={() => setShowCounselling(true)} className="inline-flex items-center justify-center bg-white/5 text-white border border-white/15 hover:bg-white/10 font-medium rounded-full px-6 py-3.5 transition backdrop-blur" data-testid="hero-counselling-btn">
                Book Free Counselling
              </button>
            </div>

            {/* Dual-mode badge strip */}
            <div className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="mode-badge-strip">
              <div className="inline-flex items-center gap-2 bg-[#FF0066]/10 border border-[#FF0066]/30 rounded-full pl-3 pr-4 py-2">
                <div className="w-7 h-7 rounded-full bg-[#FF0066]/20 flex items-center justify-center">
                  <PlayCircle className="w-3.5 h-3.5 text-[#FF0066]" />
                </div>
                <div className="text-xs">
                  <div className="font-semibold text-white leading-tight">Live Online</div>
                  <div className="text-white/60 text-[10px] leading-tight">From anywhere · India</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full pl-3 pr-4 py-2">
                <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div className="text-xs">
                  <div className="font-semibold text-white leading-tight">Indore Centre</div>
                  <div className="text-white/60 text-[10px] leading-tight">Apollo Square · Offline</div>
                </div>
              </div>
            </div>

            <div className="mt-7 flex items-center gap-4 text-sm text-white/60">
              <div className="flex -space-x-2">
                {STUDENT_AVATARS.map((src, i) => (
                  <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-[#050505] object-cover" />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-amber-400">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
                <div className="text-xs mt-0.5"><span className="font-semibold text-white/90">1,000+</span> learners trained · 4.8/5 rating</div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 text-xs">
              {[
                { icon: PlayCircle, label: "Live Practical Classes" },
                { icon: Briefcase, label: "Internship Support" },
                { icon: PlayCircle, label: "Recorded Sessions" },
                { icon: Award, label: "Certificate" },
                { icon: Users, label: "Lifetime Community" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-2 text-white/80">
                  <b.icon className="w-3.5 h-3.5 text-[#FF0066] shrink-0" /> <span className="truncate">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <img src={HERO_IMG} alt="Kamlesh Singad — Digital Marketing Trainer" className="relative w-full h-auto object-contain z-10" data-testid="hero-trainer-img" />
              <div className="absolute bottom-4 left-4 z-20 flex items-center gap-2 bg-black/70 backdrop-blur border border-white/10 rounded-xl px-3 py-2.5">
                <div className="w-10 h-10 rounded-lg bg-[#FF0066]/15 flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-[#FF0066]" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-white">Live class in progress</div>
                  <div className="text-[10px] text-white/60">Indore centre + online</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { n: "1,000+", l: "Students trained" },
            { n: "8+", l: "Years of experience" },
            { n: "4.8★", l: "Google rating" },
            { n: "100+", l: "Brands worked with" },
          ].map((s, i) => (
            <div key={i} data-testid={`trust-stat-${i}`}>
              <div className="text-3xl md:text-4xl font-extrabold text-white" style={displayStack}>{s.n}</div>
              <div className="text-xs md:text-sm text-white/50 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ WHY ============ */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">Why students choose us</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={displayStack}>Practical training that actually gets you results</h2>
            <p className="mt-3 text-white/60">Not just videos. Real campaigns. Real clients. Real career outcomes.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: GraduationCap, t: "Practical Learning", d: "Live hands-on training on real tools, dashboards and active client accounts." },
              { icon: Headphones, t: "Live Doubt Support", d: "Stuck on something? Ask in class or on WhatsApp — get help the same day." },
              { icon: Briefcase, t: "Real Client Projects", d: "Work on actual marketing campaigns and build a portfolio while you learn." },
              { icon: Trophy, t: "Career Guidance", d: "Resume reviews, interview prep and 1-on-1 career strategy sessions." },
              { icon: Users, t: "Freelancing Support", d: "First freelancing project written guarantee — start earning while studying." },
              { icon: FileCheck, t: "Resume & Interview Help", d: "Personalised resume rewrites and mock interviews with real recruiters." },
            ].map((f, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF0066]/40 hover:bg-[#161616] transition" data-testid={`why-card-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-[#FF0066]/10 flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-[#FF0066]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={displayStack}>{f.t}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ POPULAR COURSES ============ */}
      <section className="py-16 md:py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">Popular courses</div>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={displayStack}>Choose your career path</h2>
            </div>
            <Link to="/courses" className="text-sm font-medium text-[#FF0066] hover:text-[#ff3380] inline-flex items-center gap-1" data-testid="view-all-courses">View all courses <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.length === 0 && [...Array(3)].map((_, i) => <div key={i} className="bg-[#121212] border border-white/10 rounded-xl h-96 animate-pulse" />)}
            {courses.map((c) => {
              const { mrp, final, hasDiscount, percentOff, isFree } = getCoursePricing(c);
              return (
                <div key={c.id} className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden hover:border-[#FF0066]/40 transition flex flex-col" data-testid={`course-card-${c.id}`}>
                  <div className="aspect-video bg-[#0a0a0a] relative overflow-hidden">
                    {c.thumbnail ? <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><PlayCircle className="w-12 h-12 text-[#FF0066]/40" /></div>}
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/70 text-xs font-medium text-white border border-white/10">{c.category || "Beginner"}</div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-lg font-semibold text-white line-clamp-2" style={displayStack}>{c.title}</h3>
                    <div className="mt-3 flex items-center gap-3 text-xs text-white/50">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration_weeks || 8} weeks</span>
                      <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400 fill-current" /> {c.rating || 4.8}</span>
                    </div>
                    <div className="mt-4 flex items-baseline gap-2">
                      {isFree ? <span className="text-2xl font-bold text-[#FF0066]" style={displayStack}>Free</span> : (
                        <>
                          <span className="text-2xl font-bold text-white" style={displayStack} data-testid={`landing-price-${c.id}`}>{formatINR(final, { showFree: false })}</span>
                          {hasDiscount && (
                            <>
                              <span className="text-sm text-white/40 line-through" data-testid={`landing-mrp-${c.id}`}>{formatINR(mrp, { showFree: false })}</span>
                              <span className="text-xs text-emerald-300 font-semibold">{percentOff}% off</span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <Link to={`/courses/${c.slug || c.id}`} className="mt-5 inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] text-white font-medium rounded-full px-4 py-2.5 text-sm transition" data-testid={`enroll-btn-${c.id}`}>
                      View Course <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ ACTIVE BATCHES (Live Now) ============ */}
      {activeBatches.length > 0 && (
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-[#FF0066]/10 rounded-full blur-[140px] pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-semibold uppercase tracking-[0.12em] mb-3">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                  </span>
                  {activeBatches.length} {activeBatches.length === 1 ? "batch" : "batches"} running live
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={displayStack}>Live batches you can join <span className="text-[#FF0066]">right now</span></h2>
                <p className="mt-3 text-white/60">Late-joiners welcome — recordings of missed classes are available, and you'll catch up in 1 week with our onboarding plan.</p>
              </div>
              <Link to="/courses" className="text-sm font-medium text-[#FF0066] hover:text-[#ff3380] inline-flex items-center gap-1" data-testid="view-all-courses-batches">View all courses <ArrowRight className="w-4 h-4" /></Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="active-batches-grid">
              {activeBatches.map((b) => {
                const start = new Date(b.start_date);
                const end = new Date(b.end_date);
                const total = end - start;
                const elapsed = Math.min(total, Math.max(0, nowMs - start));
                const progressPct = total > 0 ? Math.round((elapsed / total) * 100) : 0;
                const daysLeft = Math.max(0, Math.ceil((end - nowMs) / 86400000));
                const seatsLeft = Math.max(0, (b.capacity || 0) - (b.enrolled_count || 0));
                const fillPct = b.capacity ? Math.min(100, Math.round((b.enrolled_count / b.capacity) * 100)) : 0;
                return (
                  <Link key={b.id} to={`/courses/${b.course_id}`} className="group bg-[#121212] border border-white/10 hover:border-[#FF0066]/40 rounded-2xl overflow-hidden flex flex-col transition" data-testid={`batch-card-${b.id}`}>
                    {/* Header */}
                    <div className="relative p-5 bg-gradient-to-br from-[#FF0066]/15 via-[#1a1a1a] to-[#0a0a0a] border-b border-white/5">
                      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-[10px] uppercase tracking-wider text-red-300 font-bold">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-80" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                          </span>
                          Live · {daysLeft}d left
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${b.mode === "offline" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : b.mode === "hybrid" ? "bg-blue-500/20 text-blue-300 border border-blue-500/40" : "bg-[#FF0066]/20 text-[#ff6699] border border-[#FF0066]/40"}`}>
                            {b.mode === "offline" ? "Offline" : b.mode === "hybrid" ? "Hybrid" : "Online"}
                          </span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${seatsLeft <= 3 ? "bg-amber-500 text-black" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>
                            {seatsLeft === 0 ? "Full" : `${seatsLeft} seats`}
                          </span>
                        </div>
                      </div>
                      <div className="text-lg font-bold text-white line-clamp-1" style={displayStack}>{b.name}</div>
                      <div className="text-xs text-white/60 line-clamp-1 mt-1">{b.course_title}</div>
                      {/* Teacher badge */}
                      <div className="mt-3 inline-flex items-center gap-2 bg-black/40 backdrop-blur border border-white/10 rounded-full pl-1 pr-3 py-1">
                        <img src={TRAINER_IMG} alt={b.teacher_name || "Kamlesh Singad"} className="w-6 h-6 rounded-full object-cover border border-[#FF0066]/40" />
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Trainer</span>
                          <span className="text-xs font-semibold text-white">{b.teacher_name || "Kamlesh Singad"}</span>
                          <ShieldCheck className="w-3 h-3 text-[#FF0066]" />
                        </div>
                      </div>
                    </div>
                    {/* Body */}
                    <div className="p-5 flex-1 flex flex-col gap-4">
                      {b.timing && (
                        <div className="flex items-start gap-2.5 text-sm text-white/80">
                          <Clock className="w-4 h-4 text-[#FF0066] mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium">{b.timing}</div>
                            <div className="text-[11px] text-white/50 mt-0.5 truncate">
                              {b.mode === "offline"
                                ? `${b.location ? `📍 ${b.location.split(",")[0]}` : "Indore Centre"} · Offline`
                                : b.mode === "hybrid"
                                ? "Online + Indore centre · Hybrid"
                                : `${b.meeting_provider || "Zoom"} · Online`}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start gap-2.5 text-sm text-white/80">
                        <Users className="w-4 h-4 text-[#FF0066] mt-0.5 shrink-0" />
                        <div>
                          <div className="font-medium">{b.enrolled_count || 0} / {b.capacity || 0} enrolled</div>
                          <div className="mt-1.5 h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-[#FF0066]" style={{ width: `${fillPct}%` }} />
                          </div>
                        </div>
                      </div>
                      {/* Course progress */}
                      <div>
                        <div className="flex justify-between text-[11px] text-white/50 mb-1.5">
                          <span>Course in progress</span>
                          <span>{progressPct}% complete</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-[#FF0066]" style={{ width: `${progressPct}%` }} />
                        </div>
                      </div>
                      <div className="mt-auto inline-flex items-center justify-between bg-[#FF0066] group-hover:bg-[#e6005c] text-white font-semibold rounded-full px-5 py-2.5 text-sm transition shadow-[0_8px_25px_-8px_rgba(255,0,102,0.5)]">
                        <span>{seatsLeft === 0 ? "Join Waitlist" : "Enroll in this Batch"}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">How it works</div>
          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={displayStack}>Get started in three simple steps</h2>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8 relative">
          {[
            { n: "01", t: "Choose Your Course", d: "Browse courses, pick what fits your goal — Digital Marketing, SEO, Graphic Design or more." },
            { n: "02", t: "Complete Enrollment", d: "Secure online payment via Razorpay. UPI, cards, wallets — all supported. Get instant access." },
            { n: "03", t: "Start Learning Live", d: "Join live classes, work on real projects, get certificate and lifetime community access." },
          ].map((s) => (
            <div key={s.n} className="relative text-center" data-testid={`step-${s.n}`}>
              <div className="w-14 h-14 rounded-full bg-[#FF0066] text-white font-bold text-lg mx-auto flex items-center justify-center mb-4 shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]" style={displayStack}>{s.n}</div>
              <h3 className="text-xl font-semibold text-white mb-2" style={displayStack}>{s.t}</h3>
              <p className="text-sm text-white/60 leading-relaxed max-w-xs mx-auto">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ RESULTS / TESTIMONIALS ============ */}
      <section id="results" className="py-16 md:py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">Student results</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={displayStack}>Real students. Real wins.</h2>
            <p className="mt-3 text-white/60">Freelancing projects, full-time placements, agency growth and personal brand success — all from our community.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Priya Sharma", role: "SEO Freelancer", text: "Within 2 months of completing the course I landed my first client at ₹15,000/month. The live practical training made all the difference.", avatar: STUDENT_AVATARS[0] },
              { name: "Rahul Verma", role: "Marketing Executive", text: "I got placed at a digital agency in Indore with a 60% salary jump. The resume review and interview prep sessions were game-changers.", avatar: STUDENT_AVATARS[1] },
              { name: "Aakash Singh", role: "Small Business Owner", text: "Sir taught me how to run Meta Ads for my own business. Now I generate 30+ leads daily and have stopped depending on agencies.", avatar: STUDENT_AVATARS[2] },
              { name: "Neha Patil", role: "Content Creator", text: "My YouTube channel grew from 1K to 22K subscribers in 4 months by applying SEO and content strategies from the course.", avatar: STUDENT_AVATARS[0] },
              { name: "Vikash Kumar", role: "WordPress Freelancer", text: "Built 8 client websites in my first month after course completion. The WordPress + SEO combo training is unmatched.", avatar: STUDENT_AVATARS[1] },
              { name: "Sneha Joshi", role: "Agency Owner", text: "Started my own digital marketing agency in Bhopal with 5 retainer clients. Kamlesh sir's mentorship continues even today.", avatar: STUDENT_AVATARS[2] },
            ].map((t, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF0066]/40 transition" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-1 text-amber-400 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-current" />)}</div>
                <p className="text-sm text-white/80 leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FACULTY ============ */}
      <FacultySection />

      {/* ============ WALL OF FAME (auto-hides if empty) ============ */}
      <WallOfFameMarquee />

      {/* ============ ABOUT TRAINER ============ */}
      <section id="about" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          {/* Content — Left */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">Meet your trainer</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={displayStack}>Kamlesh Singad</h2>
            <div className="mt-1 text-white/50">Founder · Code With Kamlesh · Digital Marketing Trainer</div>
            <p className="mt-5 text-white/70 leading-relaxed">
              Kamlesh is a digital marketing trainer, SEO consultant and the founder of Code With Kamlesh — an Indore-based digital marketing training centre. With 8+ years of hands-on industry experience, he has trained 1,000+ students, freelancers and business owners in SEO, Google Ads, Meta Ads, WordPress and AI-driven marketing.
            </p>
            <p className="mt-3 text-white/70 leading-relaxed">
              He has worked with bloggers, agencies, startups and institutions to drive measurable growth — and brings that real-client experience straight into every classroom session.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {[
                { n: "8+", l: "Years experience" },
                { n: "1,000+", l: "Students trained" },
                { n: "100+", l: "Brands & startups" },
              ].map((s, i) => (
                <div key={i} className="bg-[#121212] border border-white/10 rounded-lg p-4">
                  <div className="text-2xl font-extrabold text-white" style={displayStack}>{s.n}</div>
                  <div className="text-xs text-white/50 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/courses" className="inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] text-white font-medium rounded-full px-5 py-2.5 text-sm transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.5)]">See courses by Kamlesh <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-medium rounded-full px-5 py-2.5 text-sm transition"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>

          {/* Image — Right */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#FF0066]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] bg-[#121212]">
                <img src={TRAINER_IMG} alt="Kamlesh Singad teaching students at Code With Kamlesh, Indore" className="w-full h-full object-cover" data-testid="trainer-photo" />
                {/* Caption overlay */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                  <div className="inline-flex items-center gap-2 bg-black/60 backdrop-blur border border-white/15 rounded-full px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF0066] animate-pulse" />
                    <span className="text-xs font-medium text-white">Live mentoring · Indore centre</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-16 md:py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.16em] text-[#FF0066] font-semibold mb-3">Common questions</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={displayStack}>Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: "Is this course beginner friendly?", a: "Yes. We start from the basics and go all the way to advanced topics. No prior marketing or coding experience is required." },
              { q: "Will I get class recordings?", a: "Yes, every live class is recorded. You get lifetime access to recordings, notes and assignments through the student dashboard." },
              { q: "Is internship support available?", a: "Yes. We provide internship placement support and a written guarantee for your first freelancing project after course completion." },
              { q: "Will I receive a certificate?", a: "Yes, you receive a government-recognised completion certificate after finishing all assignments and the final project." },
              { q: "Can I attend classes from my mobile?", a: "Yes. All live classes can be joined from any device — mobile, tablet or laptop. Recordings work the same way." },
              { q: "What if the course doesn't suit me?", a: "We offer a 7-day money-back guarantee. If you're not satisfied within the first week, we will refund your full fee." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-[#121212] border border-white/10 rounded-xl px-5">
                <AccordionTrigger className="text-left text-sm md:text-base font-medium text-white hover:no-underline py-4" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-white/70 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section id="contact" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF0066] via-[#d4005a] to-[#FF0066]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight" style={displayStack}>Start your digital marketing journey today</h2>
          <p className="mt-4 text-base md:text-lg text-white/90 max-w-2xl mx-auto">Join 1,000+ students who built careers, freelance income and businesses with the skills they learnt at Code With Kamlesh.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/courses" className="inline-flex items-center justify-center bg-white text-[#FF0066] hover:bg-white/90 font-semibold rounded-full px-6 py-3.5 transition shadow-xl" data-testid="cta-enroll">
              Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <button onClick={() => setShowCounselling(true)} className="inline-flex items-center justify-center bg-black/30 hover:bg-black/50 text-white border border-white/30 font-medium rounded-full px-6 py-3.5 transition backdrop-blur" data-testid="cta-counsellor">
              Talk to Counsellor
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-white/90">
            <div className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> Apollo Square, Indore</div>
            <div className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> +91 91313 41638</div>
            <div className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> 7-day money-back guarantee</div>
          </div>
        </div>
      </section>

      <CounsellingDialog open={showCounselling} onClose={() => setShowCounselling(false)} />
    </div>
  );
}

function CounsellingDialog({ open, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", interest: "Digital Marketing", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...f, source: "landing-counselling" });
      Analytics.lead({ source: "landing-counselling", interest: f.interest });
      toast.success("Thanks! Our counsellor will call you within 24 hours.");
      onClose();
      setF({ name: "", email: "", phone: "", interest: "Digital Marketing", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to submit. Please try WhatsApp.");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border border-white/10 max-w-md text-white" style={fontStack}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white" style={displayStack}>Book a free counselling call</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-white/60 -mt-2">Our team will reach out within 24 hours to help you choose the right course.</p>
        <form onSubmit={submit} className="space-y-3 mt-3" data-testid="counselling-form">
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name *" className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]" data-testid="counsel-name" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email *" className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]" data-testid="counsel-email" />
            <input required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone *" className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]" data-testid="counsel-phone" />
          </div>
          <select value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF0066]">
            <option>Digital Marketing</option><option>SEO</option><option>Google Ads</option><option>Meta Ads</option><option>WordPress</option><option>Graphic Design</option><option>Other</option>
          </select>
          <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Anything specific? (optional)" rows={3} className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]" />
          <button disabled={submitting} className="w-full inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white font-medium rounded-full px-4 py-3 transition" data-testid="counsel-submit">
            {submitting ? "Submitting…" : "Request callback"}
          </button>
          <div className="text-center text-xs text-white/50">Or WhatsApp us directly at <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="text-[#FF0066] font-medium">+91 91313 41638</a></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
